import {
  type AbstractTemplateInOut,
  type ConfigurationExpressionValueInOut,
  isSimpleValueSetupInOut,
  isSubTemplateInOut,
  isSubTemplateValueSetupInOut,
  isTemplateHideOperation,
  isTemplateValueSetupInOut,
  type TemplateFieldValueInOut,
} from '@festina/configuration-general-types/configuration'
import type { LayerKey } from '@festina-life/api'
import {
  assertNotEmpty,
  getObjectEntries,
  notEmpty,
  type Optional,
} from '@festina-life/core'
import type { SearchMatch } from '@festina-life/ui/search'

import { fallbackPackageNameForLayer } from '../../scenes/serverConfigurationItems/helpers'
import { toIndexedPath } from '../../uiModels/helpers'
import { isParameterValueEntityOperation } from '../../uiModels/parameterOperations/helpers'
import type {
  IndexedParameterDefinitionCreateOperationWithMetaData,
  IndexedParameterValueOperations,
} from '../../uiModels/parameterOperations/types'
import type { IndexedTemplateOperationWithMetaData } from '../../uiModels/templateOperations/types'
import { functionPath } from '../../uiModels/transformers/toVisualModelConverter'
import type { ConfigurationItemsByBlock } from '../../workspace/operationsContext'
import {
  functionBody,
  functionNameWithModule,
} from '../templateFieldsTable/functionHelpers'
import {
  type ConfigurationItemSearchMatch,
  type ConfigurationItemSearchResult,
  type ConfigurationSearchItem,
  SearchCategories,
  type SearchCategoryKey,
} from './types'

export const searchResultKey = (result: ConfigurationItemSearchResult) =>
  `${result.value.id}-${result.value.operationType}-${result.value.block}-${result.value.layer.toString()}`

export const groupSearchResults = (
  ...results: ConfigurationItemSearchResult[]
) => {
  const processedKeys = new Map<string, ConfigurationItemSearchResult>()

  results.forEach((result) => {
    const resultKey = searchResultKey(result)
    const processedResult = processedKeys.get(resultKey)

    if (!processedResult) {
      processedKeys.set(resultKey, result)
    } else {
      const processedMatches = [...(processedResult.matches ?? [])]

      result.matches?.forEach((resultMatch) => {
        if (
          !processedMatches.some(
            (match) =>
              match.path === resultMatch.path &&
              match.value === resultMatch.value,
          )
        ) {
          processedMatches.push(resultMatch)
        }
      })

      processedKeys.set(resultKey, {
        ...processedResult,
        matches: processedMatches,
      })
    }
  })

  return Array.from(processedKeys.values()).sort((a, b) => {
    const aPathCount = a.matches?.length ?? 0
    const bPathCount = b.matches?.length ?? 0

    if (aPathCount === bPathCount) {
      return a.value.id
        .toLocaleLowerCase()
        .localeCompare(b.value.id.toLocaleLowerCase())
    }

    return bPathCount - aPathCount
  })
}

export const propertyMatchesForQueryInOperation = (
  indexedOperation: IndexedTemplateOperationWithMetaData,
  query: string,
): [
  IndexedTemplateOperationWithMetaData,
  SearchMatch<ConfigurationItemSearchMatch, SearchCategoryKey>[],
] => {
  if (isTemplateHideOperation(indexedOperation.internalOperation)) {
    return [indexedOperation, []]
  }

  assertNotEmpty(indexedOperation.internalOperation.entity?.name)

  const matchedPaths: SearchMatch<
    ConfigurationItemSearchMatch,
    SearchCategoryKey
  >[] = []
  const basePath = `{${indexedOperation.internalOperation.entity.name}}`

  registerPropertyMatchesInsideAbstractTemplateInOut({
    template: indexedOperation.internalOperation.entity,
    templatePath: basePath,
    matches: matchedPaths,
    query,
  })

  return [indexedOperation, matchedPaths]
}

const registerPropertyMatchesInsideAbstractTemplateInOut = ({
  template,
  query,
  templatePath,
  matches,
}: {
  template: AbstractTemplateInOut
  query: string
  templatePath: string
  matches: SearchMatch<ConfigurationItemSearchMatch, SearchCategoryKey>[]
}) => {
  if (
    template.name?.toLocaleLowerCase().includes(query) &&
    isSubTemplateInOut(template)
  ) {
    matches.push({
      categories: [SearchCategories.properties],
      path: templatePath,
      value: {
        isExpression: false,
        match: template.name,
        propertyPath: templatePath,
      },
    })
  }

  template.properties?.filter(notEmpty).forEach((property) => {
    assertNotEmpty(property.name)

    const propertyPath = `${templatePath}.${property.name}`

    if (property.name.toLocaleLowerCase().includes(query)) {
      matches.push({
        categories: [SearchCategories.properties],
        path: propertyPath,
        value: { isExpression: false, match: property.name, propertyPath },
      })
    }

    if (property.value && isSubTemplateValueSetupInOut(property.value)) {
      property.value.variants?.filter(notEmpty).forEach((variant) => {
        registerPropertyMatchesInsideAbstractTemplateInOut({
          template: variant,
          query,
          templatePath: `${propertyPath}{${variant.name}}`,
          matches,
        })
      })
    }
  })

  const allFunctions = [
    ...(template.dynamicFunctions ?? []),
    ...(template.declaredFunctions ?? []),
  ].filter(notEmpty)

  allFunctions.forEach((functionDeclaration) => {
    const source = functionDeclaration.function
    const path = functionPath({
      basePath: templatePath,
      source,
    })

    const name = functionNameWithModule(functionDeclaration.function)

    if (source && name.toLocaleLowerCase().includes(query)) {
      matches.push({
        categories: [SearchCategories.properties],
        path,
        value: { isExpression: true, match: source, propertyPath: path },
      })
    }
  })
}

export const expressionMatchesForQueryInTemplateOperation = (
  indexedOperation: IndexedTemplateOperationWithMetaData,
  query: string,
): [
  IndexedTemplateOperationWithMetaData,
  SearchMatch<ConfigurationItemSearchMatch, SearchCategoryKey>[],
] => {
  if (isTemplateHideOperation(indexedOperation.internalOperation)) {
    return [indexedOperation, []]
  }

  assertNotEmpty(indexedOperation.internalOperation.entity?.name)

  const matchedPaths: SearchMatch<
    ConfigurationItemSearchMatch,
    SearchCategoryKey
  >[] = []
  const basePath = `{${indexedOperation.internalOperation.entity.name}}`

  registerExpressionMatchesInsideAbstractTemplateInOut({
    template: indexedOperation.internalOperation.entity,
    templatePath: basePath,
    matches: matchedPaths,
    query,
  })

  return [indexedOperation, matchedPaths]
}

const registerExpressionMatchesInsideAbstractTemplateInOut = ({
  template,
  query,
  templatePath,
  matches,
}: {
  template: AbstractTemplateInOut
  query: string
  templatePath: string
  matches: SearchMatch<ConfigurationItemSearchMatch, SearchCategoryKey>[]
}) => {
  // Function matches
  ;[...(template.declaredFunctions ?? []), ...(template.dynamicFunctions ?? [])]
    .map((functionValueInOut) => functionValueInOut?.function)
    .filter(notEmpty)
    .forEach((value) => {
      if (
        isQueryInExpression({ query, expression: functionBody(value) ?? '' })
      ) {
        const propertyPath = functionPath({
          basePath: templatePath,
          source: value,
        })
        matches.push({
          categories: [SearchCategories.fplus],
          path: propertyPath,
          value: {
            isExpression: true,
            match: value,
            propertyPath,
          },
        })
      }
    })

  updateMatchesForExpressions({
    value: template,
    matches,
    basePath: templatePath,
    propertyPath: templatePath,
    query,
  })

  template.properties?.filter(notEmpty).forEach((property) => {
    if (property.value) {
      registerExpressionMatchesInsideTemplateFieldValueInOut({
        matches,
        value: property.value,
        propertyPath: `${templatePath}.${property.name}`,
        query,
      })
    }
  })
}

const registerExpressionMatchesInsideTemplateFieldValueInOut = ({
  propertyPath,
  value,
  matches,
  query,
}: {
  propertyPath: string
  value: TemplateFieldValueInOut
  matches: SearchMatch<ConfigurationItemSearchMatch, SearchCategoryKey>[]
  query: string
}) => {
  updateMatchesForExpressions({
    value,
    matches,
    query,
    basePath: propertyPath,
    propertyPath,
  })

  if (
    isSimpleValueSetupInOut(value) ||
    isSubTemplateValueSetupInOut(value) ||
    isSimpleValueSetupInOut(value)
  ) {
    const validations = value.validations

    validations?.filter(notEmpty).forEach((validation, index) => {
      updateMatchesForExpressions({
        value: validation,
        matches,
        query,
        basePath: toIndexedPath({
          basePath: propertyPath,
          index,
          propName: 'validations',
        }),
        propertyPath,
      })
    })
  }

  if (isSimpleValueSetupInOut(value)) {
    if (value.frame) {
      const frame = value.frame

      updateMatchesForExpressions({
        value: frame,
        matches,
        query,
        basePath: `${propertyPath}.frame`,
        propertyPath,
      })
    }

    if (value.dynamicFunctions) {
      ;[...(value.dynamicFunctions ?? [])]
        .map((functionValueInOut) => functionValueInOut?.function)
        .filter(notEmpty)
        .forEach((value) => {
          if (isQueryInExpression({ query, expression: value })) {
            matches.push({
              categories: [SearchCategories.fplus],
              path: propertyPath,
              value: {
                isExpression: true,
                match: value,
                propertyPath,
              },
            })
          }
        })
    }
  } else if (isSubTemplateValueSetupInOut(value)) {
    value.variants?.filter(notEmpty).forEach((variant) => {
      assertNotEmpty(variant.name)

      registerExpressionMatchesInsideAbstractTemplateInOut({
        template: variant,
        query,
        matches,
        templatePath: `${propertyPath}{${variant.name}}`,
      })
    })
  } else if (isTemplateValueSetupInOut(value)) {
    value.variants?.filter(notEmpty).forEach((variant, index) => {
      updateMatchesForExpressions({
        value: variant,
        query,
        basePath: toIndexedPath({
          basePath: propertyPath,
          index,
          propName: 'variants',
        }),
        matches,
        propertyPath,
      })
    })
  } else if (
    isConfigurationExpressionValueInOut(value) &&
    value.expression &&
    isQueryInExpression({ query, expression: value.expression })
  ) {
    matches.push({
      categories: [SearchCategories.fplus],
      path: propertyPath,
      value: {
        isExpression: true,
        match: value.expression,
        propertyPath,
      },
    })
  }
}

const updateMatchesForExpressions = <T extends Record<string, unknown>>({
  value,
  matches,
  query,
  basePath,
  propertyPath,
}: {
  value: T
  matches: SearchMatch<ConfigurationItemSearchMatch, SearchCategoryKey>[]
  query: string
  basePath: string
  propertyPath: string
}) => {
  getObjectEntries(value).forEach(([key, keyValue]) => {
    if (
      isConfigurationExpressionValueInOut(keyValue) &&
      keyValue.expression &&
      isQueryInExpression({ query, expression: keyValue.expression })
    ) {
      matches.push({
        categories: [SearchCategories.fplus],
        path: `${basePath}.${key.toString()}`,
        value: {
          isExpression: true,
          match: keyValue.expression,
          propertyPath,
        },
      })
    }
  })
}

const isConfigurationExpressionValueInOut = (
  value: unknown,
): value is ConfigurationExpressionValueInOut =>
  !!value &&
  typeof value === 'object' &&
  '_type' in value &&
  value['_type'] === 'ConfigurationExpressionValueInOut'

export const sanitiseExpression = (expression: string) =>
  expression.replace(/[\n\t\r]/g, ' ').replace(/\s+/g, ' ')

const isQueryInExpression = ({
  query,
  expression,
}: {
  query: string
  expression: string
}) => sanitiseExpression(expression).toLocaleLowerCase().includes(query)

export const expressionMatchesForQueryInParameterDefinitionOperation = (
  indexedOperation: IndexedParameterDefinitionCreateOperationWithMetaData,
  query: string,
): [
  IndexedParameterDefinitionCreateOperationWithMetaData,
  SearchMatch<ConfigurationItemSearchMatch, SearchCategoryKey>[],
] => {
  const parameterDefinitionId = indexedOperation.internalOperation.entity?.name
  assertNotEmpty(parameterDefinitionId)

  const matchedPaths: SearchMatch<
    ConfigurationItemSearchMatch,
    SearchCategoryKey
  >[] = []
  const basePath = `{${parameterDefinitionId}}`

  indexedOperation.internalOperation.entity?.dimensionDefinitions
    ?.filter(notEmpty)
    .forEach((definition) => {
      if (
        definition.dimensionExpression &&
        isQueryInExpression({
          query,
          expression: definition.dimensionExpression,
        })
      ) {
        matchedPaths.push({
          categories: [SearchCategories.fplus],
          path: `${basePath}.${definition.name}`,
          value: {
            isExpression: true,
            match: definition.dimensionExpression,
          },
        })
      }
    })

  return [indexedOperation, matchedPaths]
}

/** A parameter is only defined for the block and layer its definition belongs to. */
const definedParameterKey = ({
  block,
  layer,
  parameterId,
}: {
  block: string
  layer: LayerKey
  parameterId: Optional<string>
}) => `${block}-${layer.toString()}-${parameterId}`

const toValueOnlyParameterSearchItem = ({
  parameterId,
  block,
  layer,
  valueOperations,
  packageName,
}: {
  parameterId: string
  block: string
  layer: LayerKey
  valueOperations: IndexedParameterValueOperations
  packageName: string
}): ConfigurationSearchItem | undefined => {
  const activeOperations = valueOperations.filter(
    ({ annulOperations }) => !annulOperations.length,
  )

  if (!activeOperations.length) {
    return undefined
  }

  const firstNonLargeOperation = activeOperations
    .map(({ internalOperation }) => internalOperation)
    .find(isParameterValueEntityOperation)

  return {
    id: parameterId,
    operationType: 'parameterDefinition',
    type: firstNonLargeOperation?.entity?.parameterType,
    layer,
    block,
    packageName,
    isDraft: activeOperations.some(({ isDraft }) => isDraft),
  } satisfies ConfigurationSearchItem
}

export const valueOnlyParameterSearchItems = ({
  definitionOperations,
  parameterValueEntityOperations,
  fallbackPackageName,
}: {
  definitionOperations: IndexedParameterDefinitionCreateOperationWithMetaData[]
  parameterValueEntityOperations: ConfigurationItemsByBlock<IndexedParameterValueOperations>
  fallbackPackageName: string
}): ConfigurationSearchItem[] => {
  const definedParameters = new Set(
    definitionOperations.map(({ block, layer, internalOperation }) =>
      definedParameterKey({
        block,
        layer,
        parameterId: internalOperation.entity?.name,
      }),
    ),
  )

  return Array.from(parameterValueEntityOperations.entries()).flatMap(
    ([block, valuesByLayer]) =>
      Array.from(valuesByLayer.entries()).flatMap(([layer, valuesById]) =>
        Array.from(valuesById.entries())
          .filter(
            ([parameterId]) =>
              !definedParameters.has(
                definedParameterKey({ block, layer, parameterId }),
              ),
          )
          .map(([parameterId, valueOperations]) =>
            toValueOnlyParameterSearchItem({
              parameterId,
              block,
              layer,
              valueOperations,
              packageName:
                fallbackPackageNameForLayer({
                  layer,
                  packageName: fallbackPackageName,
                }) ?? '',
            }),
          )
          .filter(notEmpty),
      ),
  )
}
